import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { axiosGet } from '../utils/axiosGet';
import { getCollectionRowNames } from '../utils/getCollectionRowNames';
import { useLoggedInUserContextProvider } from './LoggedInUserContext';
import { filteringTableFormatDraftNomination } from '../utils/filteringTableFormatDraftNomination';

const ProposerCollections = createContext();

export const ProposerCollectionsProvider = ({ children }) => {
  const [candidates, setCandidates] = useState([]);
  const [proposerNominationIdArray, setProposerNominationIdArray] = useState(
    [],
  );
  const [userNominationArray, setUserNominationArray] = useState([]);
  const [userDraftNominationArray, setUserDraftNominationArray] = useState([]);
  const [nominationStateArray, setNominationStateArray] = useState([]);
  const [refreshProposerCollections, setRefreshProposerCollections] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { userDetails, hasCheckedAuth } = useLoggedInUserContextProvider();

  const memoizedUser = useMemo(() => userDetails, [userDetails?.id]);

  useEffect(() => {
    const fetchData = async () => {
      if (!hasCheckedAuth) {
        setIsLoading(true);
        return;
      }

      if (!memoizedUser) {
        setUserNominationArray([]);
        setNominationStateArray([]);
        setCandidates([]);
        setUserDraftNominationArray([]);
        setProposerNominationIdArray([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const [secondersRes, draftsRes] = await Promise.all([
          axiosGet(
            `proposer-seconders?filters[proposerUserId][$eq]=${memoizedUser.id}`,
          ),
          axiosGet(`nomination-drafts?filters[userId][$eq]=${memoizedUser.id}`),
        ]);

        const formattedDraftNomination = filteringTableFormatDraftNomination(
          draftsRes?.data?.data,
        );
        setUserDraftNominationArray(formattedDraftNomination || []);

        let nominationIds = [];
        if (secondersRes && secondersRes?.data?.data?.length > 0) {
          if (secondersRes.data.data.length > 1) {
            nominationIds = secondersRes.data.data.map(
              (item) => item.attributes.nominationId,
            );
          } else {
            const proposerSingleNominationId =
              secondersRes.data.data[0].attributes.nominationId;
            nominationIds = [proposerSingleNominationId];
          }
        }
        setProposerNominationIdArray(nominationIds);

        if (nominationIds.length > 0) {
          const filteringQuery = nominationIds
            .map((id) => `filters[id][$in]=${id}`)
            .join('&');

          const nominationStateFilterQuery = nominationIds
            .map((id) => `filters[nominationId][$in]=${id}`)
            .join('&');

          const [statesRes, nominationsRes] = await Promise.all([
            axiosGet(`nomination-states?${nominationStateFilterQuery}`),
            axiosGet(`nominations?${filteringQuery}`),
          ]);

          setNominationStateArray(statesRes?.data?.data || []);

          if (
            nominationsRes?.data?.data &&
            nominationsRes.data.data.length !== 0
          ) {
            setUserNominationArray(nominationsRes.data.data);

            const candidateIds = nominationsRes.data.data.map(
              (item) => item.attributes.candidateId,
            );
            const candidateQueryString = candidateIds
              .map((id) => `filters[id][$in]=${id}`)
              .join('&');

            const candidatesResponse = await axiosGet(
              `candidates?${candidateQueryString}`,
            );
            setCandidates(
              getCollectionRowNames(candidatesResponse?.data?.data),
            );
          } else {
            // Fallback checking
            const nominationDraftResponse = await axiosGet(
              `nomination-drafts?filters[userId][$eq]=${memoizedUser.id}`,
            );
            if (!nominationDraftResponse?.data?.data) {
              const secondResponse = await axiosGet(
                `nominations?${filteringQuery}`,
              );
              if (
                secondResponse?.data?.data &&
                secondResponse.data.data.length !== 0
              ) {
                setUserNominationArray(secondResponse.data.data);

                const candidateIds = secondResponse.data.data.map(
                  (item) => item.attributes.candidateId,
                );
                const candidateQueryString = candidateIds
                  .map((id) => `filters[id][$in]=${id}`)
                  .join('&');

                const candidatesResponse = await axiosGet(
                  `candidates?${candidateQueryString}`,
                );
                setCandidates(
                  getCollectionRowNames(candidatesResponse?.data?.data),
                );
              } else {
                setUserNominationArray([]);
                setCandidates([]);
              }
            } else {
              setUserNominationArray([]);
              setCandidates([]);
            }
          }
        } else {
          setUserNominationArray([]);
          setNominationStateArray([]);
          setCandidates([]);
        }
      } catch (error) {
        console.error('Error loading proposer collections:', error);
        setUserNominationArray([]);
        setNominationStateArray([]);
        setCandidates([]);
        setUserDraftNominationArray([]);
        setProposerNominationIdArray([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [memoizedUser, hasCheckedAuth, refreshProposerCollections]);

  return (
    <ProposerCollections.Provider
      value={useMemo(
        () => ({
          candidates,
          userNominationArray,
          userDraftNominationArray,
          nominationStateArray,
          refreshProposerCollections,
          setRefreshProposerCollections,
          isLoading,
        }),
        [
          candidates,
          userNominationArray,
          userDraftNominationArray,
          nominationStateArray,
          refreshProposerCollections,
          setRefreshProposerCollections,
          isLoading,
        ],
      )}
    >
      {children}
    </ProposerCollections.Provider>
  );
};

ProposerCollectionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useProposerCollections = () => useContext(ProposerCollections);
